import {useState, useRef, useEffect, useCallback, useMemo} from "@wordpress/element";
import {useSelect, useDispatch} from '@wordpress/data';
import {CART_STORE_KEY} from '@woocommerce/block-data';
import {getSetting} from '@woocommerce/settings';
import {isEmail} from '@wordpress/url';
import {useCreateFastlane, useProcessFastlane} from "../hooks";
import {useDeviceData} from "../../hooks";
import {PaymentMethodDataProvider} from "../../context";
import {getSettings, getClientToken} from "../../utils";

const Component = ({extensions}) => {
    const isInitialRender = useRef(true);
    const currentData = useRef({});
    const data = extensions.wc_braintree.fastlane;

    const {
        needsShipping,
        billingAddress
    } = useSelect(select => {
        const store = select(CART_STORE_KEY);

        return {
            needsShipping: store.getNeedsShipping(),
            ...store.getCustomerData()
        }
    });

    const {
        setShippingAddress
    } = useDispatch(CART_STORE_KEY);

    const {
        email
    } = billingAddress;

    const [debouncedEmail, setDebouncedEmail] = useState(email);

    const dataCollector = useDeviceData({skip: true});

    const fastlane = useCreateFastlane({
        clientToken: getClientToken(),
        fraudEnabled: data.fraud.enabled,
        dataCollector
    });

    const onError = useCallback(() => {

    }, []);

    const shippingData = useMemo(() => {
        return {
            needsShipping,
            setShippingAddress
        }
    }, [needsShipping, setShippingAddress]);

    const {
        emails,
        isCustomerFound,
        triggerAuthentication
    } = useProcessFastlane({
        i18n: data.i18n,
        email,
        onError,
        fastlane,
        shippingData,
        openFastlaneModal: null
    });

    useEffect(() => {
        currentData.current = {emails, fastlane, isCustomerFound};
    }, [emails, fastlane, isCustomerFound]);

    useEffect(() => {
        // Set a timer to update the debounced value after 500ms
        const timer = setTimeout(() => {
            setDebouncedEmail(email);
        }, 500); // Adjust debounce time as needed (500ms is common)

        // Clean up the timer if email changes before timeout completes
        return () => clearTimeout(timer);
    }, [email]);

    // Effect for handling email changes
    useEffect(() => {
        const {
            emails,
            isCustomerFound
        } = currentData.current;

        // If customer cancelled, don't try to authenticate.
        if (emails.includes(debouncedEmail) || isCustomerFound) {
            return;
        }
        // Don't do anything if requirements aren't met
        if (!fastlane || !triggerAuthentication || !isEmail(debouncedEmail)) {
            return;
        }

        // Only trigger on first load if fastlane_pageload is true
        if (isInitialRender.current) {
            isInitialRender.current = false;
            if (data.fastlane_pageload) {
                triggerAuthentication(debouncedEmail, false);
            }
            return;
        }

        // If we get here, email has changed and is valid, so trigger authentication
        triggerAuthentication(debouncedEmail, false);
    }, [fastlane, debouncedEmail, triggerAuthentication]);

    useEffect(() => {
        const {
            fastlane
        } = currentData.current;

        if (!fastlane) {
            return;
        }
        let el = document.getElementById('email');
        if (el) {
            let container = document.getElementById('wc-ppcp-watermark-container');
            if (container) {
                return;
            }
            container = document.createElement('div');
            container.id = 'wc-ppcp-watermark-container';

            fastlane.FastlaneWatermarkComponent({includeAdditionalInfo: true}).then(component => {
                el.parentElement.append(container);
                component.render('#wc-ppcp-watermark-container');
            });
        }
    });

    return null;
}

export const Block = (props) => {
    const getData = getSettings('braintree_cc');
    return (
        <PaymentMethodDataProvider getData={getData}>
            <Component {...props}/>
        </PaymentMethodDataProvider>
    );
}